import React, {useCallback, useState} from "react";
import {IconButton} from "../../../features/management-v2/import/inputs";
import BasicText from "../../../components/form-v2/basic-text";
import SaveIcon from "@mui/icons-material/Save";
import {platformAPI} from "../../../api/axiosConfig";
import {useNavigateWithClientID} from "../hooks/useNavigateWithClientID";
import {useSiren} from "../SirenContext";
import {HotlineNumber} from "../typedefs";
import {AudioFileSelect} from "../components/AudioFileSelect";
import {AudioFilePlayer} from "../components/AudioFilePlayer";
import {PaginatedSelector} from "../components/PaginatedSelector";
import {fetchAvailableNumbers} from "../hooks/useGetNumbers";
import {formatPhoneNumber} from "../../../lib/pnone/formatPhoneNumber";


export function HotlineCreate() {

    const {navigateWithClientID} = useNavigateWithClientID();
    const {clientID} = useSiren()

    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveErrors, setSaveErrors] = useState<Record<string, string[]>>({});
    const [saving, setSaving] = useState<boolean>(false);
    const [audioFileID, setAudioFileID] = useState<string>();



    const [name, setName] = useState<string>("")
    const [selectedNumberIDs, setSelectedNumberIDs] = useState<string[]>([]);
    const [startTime, setStartTime] = useState<string>("00:00")
    const [startDate, setStartDate] = useState<string>("")

    const [endTime, setEndTime] = useState<string>("00:00")
    const [endDate, setEndDate] = useState<string>("")





    const save = useCallback(async () => {
        setSaving(true);
        setSaveError(null);
        setSaveErrors({});

        try {
            const start = startDate ? new Date(`${startDate}T${startTime}`) : null;
            const end = endDate ? new Date(`${endDate}T${endTime}`) : null;

            const request = {
                clientID,
                name,
                startTime: start?.toISOString() ?? "",
                endTime: end?.toISOString() ?? "",
                numberIDs: selectedNumberIDs,
                audioFileID,
            };

            await platformAPI.post("api/hotlines", request);
            navigateWithClientID("/siren/hotlines?subPage=hotlines");
        } catch (e: any) {
            console.error(e);
            const errors = e?.response?.data?.errors;
            if (errors && typeof errors === "object") {
                setSaveErrors(errors);
            } else {
                setSaveError(e?.response?.data?.error ?? "Error creating hotline");
            }
        } finally {
            setSaving(false);
        }
    }, [name, startDate, startTime, endDate, endTime, selectedNumberIDs, audioFileID, clientID]);

    const getNumbers = useCallback((search: string, page: number) => {
        const start = startDate && startTime ? new Date(`${startDate}T${startTime}`).toISOString() : ""
        const end = endDate && endTime ? new Date(`${endDate}T${endTime}`).toISOString() : ""
        return fetchAvailableNumbers(page, 10, search, start, end)
    }, [startDate, startTime, endDate, endTime])

    const getNumberColumns = useCallback((item: HotlineNumber) => {
        return [
            item.name,
            formatPhoneNumber(item.phoneNumber.e164),
        ]
    }, [])


    const timesSet = !!(startDate && startTime && endDate && endTime);
    const selectorKey = `${startDate}-${startTime}-${endDate}-${endTime}`;

    return (
        <div className="bg-white shadow-md p-4 flex flex-col gap-4">

            <h1 className={"text-lg font-semibold"}>Create Hotline Number</h1>


            <BasicText label={"Name"} value={name} onChange={setName} required/>


            <label className={"mb-1"}>Select Audio File</label>
            <AudioFileSelect clientID={clientID} value={audioFileID}
                             onChange={(f) => setAudioFileID(f?.id)}/>


            {audioFileID && (
                <div className={"flex flex-col gap-4"}>
                    <AudioFilePlayer id={audioFileID}/>
                </div>
            )}


            <div className={"grid grid-cols-2 gap-4 mb-4"}>
                <BasicText label={"Start Date"} type={"date"} value={startDate} onChange={setStartDate}/>
                <BasicText label={"Start Time"} type={"time"} value={startTime} onChange={setStartTime}/>
            </div>

            <div className={"grid grid-cols-2 gap-4 mb-4"}>
                <BasicText label={"End Date"} type={"date"} value={endDate} onChange={setEndDate}/>
                <BasicText label={"End Time"} type={"time"} value={endTime} onChange={setEndTime}/>
            </div>


            {timesSet && (
                <>
                    <label className={"font-medium"}>Select Phone Numbers ({selectedNumberIDs.length} selected)</label>
                    <PaginatedSelector
                        key={selectorKey}
                        headers={["Name", "Phone Number"]}
                        selectedIDs={selectedNumberIDs}
                        setSelectedIDs={setSelectedNumberIDs}
                        getItems={getNumbers}
                        getColumns={getNumberColumns}
                    />
                </>
            )}


            <IconButton onClick={save} text={"Create"} loading={saving} icon={<SaveIcon/>}/>

            {saveError && (
                <p className={"text-center text-red"}>{saveError}</p>
            )}
            {Object.entries(saveErrors).map(([field, msgs]) => (
                <p key={field} className={"text-center text-red"}>{msgs[0]}</p>
            ))}


        </div>
    )
}
